// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { css } from "styled-components";

export interface PreTitleStyles {
    wrapper?: InjectableCss;
    text?: TypographyPresentationProps;
}

export interface PreTitlePresentationProps extends ExpandStyles<PreTitleStyles> {}

export interface PreTitleProps extends PreTitlePresentationProps {
    text?: string;
    dotSize?: number;
    variant?: TypographyProps["variant"];
    forwardAs?: TypographyProps["forwardAs"];
    className?: string;
}

export const preTitleStyles: PreTitleStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(8)};
            margin: 0;

            --pre-title-dot-size: ${pxToRem(10)};

            &::before {
                content: "";
                width: var(--pre-title-dot-size);
                height: var(--pre-title-dot-size);
                border-radius: ${pxToRem(999)};
                background: ${getColor("primary")};
                display: inline-block;
                flex: 0 0 auto;
            }
        `,
    },
    text: {
        variant: "p",
        css: css`
            margin: 0;
        `,
    },
};

type PreTitleCssVars = React.CSSProperties & {
    ["--pre-title-dot-size"]?: string;
};

const PreTitle: React.FC<PreTitleProps> = ({
    text,
    dotSize = 10,
    variant,
    forwardAs,
    className,
    extendedStyles,
    overridedStyles,
}) => {
    if (!text) {
        return null;
    }

    const styles = useExpandProps(preTitleStyles, extendedStyles, overridedStyles);

    const safeDotSize = Number.isFinite(dotSize) && dotSize > 0 ? dotSize : 10;

    const dotSizeStyle = React.useMemo<PreTitleCssVars>(
        () => ({ "--pre-title-dot-size": `${safeDotSize}px` }),
        [safeDotSize],
    );

    const textProps = React.useMemo(() => {
        if (!variant && !forwardAs) {
            return styles.text;
        }

        return mergeProps<PreTitleStyles["text"]>(styles.text, {
            variant,
            forwardAs,
        });
    }, [styles.text, variant, forwardAs]);

    return (
        <StyledWrapper {...styles.wrapper} className={className ?? "preTitleRow"} style={dotSizeStyle}>
            <Typography {...textProps}>{text}</Typography>
        </StyledWrapper>
    );
};

export default PreTitle;
